import type { BillingPlan, EngineSubscription } from '../../services/billingApi';

/**
 * Plan-expiry rules, kept OUT of PlanSummaryCard.tsx so they are testable:
 * that component imports the billing API, which pulls in Firebase, which
 * jsdom cannot load (house rule: RTL leaf components only).
 */

/** Annual SKUs are one-shot Orders with no mandate. Derived from the
 *  plan_code suffix the catalog guarantees (`<vertical>_<tier>_<period>`). */
export function isAnnual(planCode: string | undefined): boolean {
  return !!planCode && planCode.endsWith('_annual');
}

/**
 * Does this subscription renew by itself? Only with a live mandate — the
 * engine says so in `auto_renews` (2026-10-18). Monthly plans can now be
 * PREPAID (one-time payment, renewed by the lawyer) while Razorpay auto-debit
 * is not enabled, so "monthly" no longer implies "renews". An older engine
 * without the field falls back to the old rule (annual = no mandate).
 */
export function autoRenews(sub: EngineSubscription | undefined): boolean {
  if (!sub) return false;
  return sub.auto_renews ?? !isAnnual(sub.plan_code);
}

/** Whether buying `plan` now sets up auto-renewal (engine `auto_renews`). */
export function autoRenewsOnPurchase(plan: BillingPlan): boolean {
  return plan.auto_renews ?? plan.billing_period === 'monthly';
}

/** " each month." / " for one month." / " for the year." — never imply a
 *  recurring charge that will not happen. */
export function chargePeriodPhrase(plan: BillingPlan): string {
  if (plan.billing_period === 'annual') return ' for the year.';
  return autoRenewsOnPurchase(plan) ? ' each month.' : ' for one month.';
}

/** A renewal has landed when the renewed plan's period end moved past the end
 *  it had when the lawyer clicked Renew.
 *
 *  This still holds under the 2026-11-01 reset semantics (a renewal resets
 *  the period to payment_date + one plan length, rather than stacking onto
 *  the old end): the new end is `payment_date + period_length`, and the old
 *  end (captured at click time) is `original_period_start + period_length`.
 *  Since `payment_date >= original_period_start` always holds for a still-
 *  active subscription, the new end is always >= the old end — equality
 *  only in the vanishing edge case of paying at the exact instant the old
 *  period started, which the strict `>` below simply treats as "not yet
 *  landed" rather than over-reporting. */
export function isRenewed(
  subs: EngineSubscription[],
  vertical: string,
  renewal: { planCode: string; currentPeriodEnd: string | null },
): boolean {
  const before = renewal.currentPeriodEnd ? new Date(renewal.currentPeriodEnd).getTime() : 0;
  return subs.some(
    (s) =>
      s.vertical === vertical &&
      s.plan_code === renewal.planCode &&
      s.status === 'active' &&
      !!s.current_period_end &&
      new Date(s.current_period_end).getTime() > before,
  );
}

/** Whole days from now until `iso`; null if unparseable, negative if past. */
export function daysUntil(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
}

/**
 * When to warn that a plan is about to lapse.
 *
 * ANNUAL plans genuinely expire — they are one-shot Orders with no mandate,
 * so access simply stops unless the lawyer buys again. The engine's reminder
 * cron only covers TRIALS (`emails.py` filters on `trial_end IS NOT NULL`),
 * and SMTP is unconfigured anyway (NullEmailer logs instead of sending), so
 * an email-only warning would reach nobody. This in-product notice is the
 * warning that actually works today.
 *
 * Plans with a mandate are excluded: they renew on their own, so a countdown
 * there would be a false alarm. Prepaid monthly plans have no mandate and
 * DO lapse, so they are warned too (2026-10-18).
 *
 * 30 days for annual is deliberately generous — a lawyer near their slot cap
 * may need to close matters, and renewal is a purchase decision, not a click.
 * A prepaid month warns in its last 7 days (30 would mean always).
 */
export const EXPIRY_WARNING_DAYS = 30;
export const MONTHLY_EXPIRY_WARNING_DAYS = 7;

export function shouldWarnExpiry(
  sub: EngineSubscription | undefined,
  now: Date = new Date(),
): boolean {
  if (!sub || autoRenews(sub)) return false;
  if (sub.cancel_at_period_end) return false; // already stated as cancelling
  const days = daysUntil(sub.current_period_end, now);
  const windowDays = isAnnual(sub.plan_code) ? EXPIRY_WARNING_DAYS : MONTHLY_EXPIRY_WARNING_DAYS;
  return days !== null && days >= 0 && days <= windowDays;
}
